import type { RefObject } from 'react'
import { Link } from 'react-router-dom'
import type { Request, RequestItem } from '../../../shared/api/types'
import { ProcurementActions } from './ProcurementActions'
import { KpModePanel } from './KpModePanel'
import { PricingNotReady } from './PricingNotReady'
import type { SelectedPrice } from '../hooks/useKpMode'
import type { PricingReadiness } from '../hooks/useRequestRelatedData'
import type { MarkupConstants as MarkupConstantsBase } from '../../../shared/api/exchangeRates.api'

type ExchangeRates = { USD: number; EUR: number; CNY: number } | undefined
type MarkupConstants = MarkupConstantsBase | undefined

type Props = {
  request: Request
  isProcurement: boolean
  /** РОЗ: вместо менеджерских «Выставить КП»/«Выбор квот» — «Все квоты»,
      как у закупщика (создавать КП и сохранять выбор квот РОЗ не может). */
  isRos: boolean
  /** ГД смотрит заявку только на чтение: кнопки «Выставить КП» у него нет
      (решение Сергея 16.10.2026, сервер всё равно отвечает 403). */
  canCreateKp: boolean
  visibleItems: RequestItem[]
  // Upload state
  uploadRef: RefObject<HTMLInputElement>
  isUploading: boolean
  uploadResult: string | null
  onUpload: (e: React.ChangeEvent<HTMLInputElement>) => void
  // KP state
  kpMode: boolean
  /** Вход в режим КП: включает режим и предзаполняет выбор из «Выбора квот». */
  onEnterKp: () => void
  selectedPrices: Map<string, SelectedPrice>
  setSelectedPrices: React.Dispatch<React.SetStateAction<Map<string, SelectedPrice>>>
  kpMarkups: Record<string, number>
  setKpMarkups: React.Dispatch<React.SetStateAction<Record<string, number>>>
  positionOverrides: Record<string, number>
  setPositionOverrides: React.Dispatch<React.SetStateAction<Record<string, number>>>
  showOverrides: boolean
  setShowOverrides: React.Dispatch<React.SetStateAction<boolean>>
  onCancelKp: () => void
  exchangeRates: ExchangeRates
  markupConstants: MarkupConstants
  /** Готовность курсов и коэффициентов. Пока не готовы — вместо кнопки
      «Выставить КП» показываем плашку с кнопкой «Повторить». */
  pricing: PricingReadiness
}

export function ItemsActionsRow({
  request,
  isProcurement,
  isRos,
  canCreateKp,
  visibleItems,
  uploadRef,
  isUploading,
  uploadResult,
  onUpload,
  kpMode,
  onEnterKp,
  selectedPrices,
  setSelectedPrices,
  kpMarkups,
  setKpMarkups,
  positionOverrides,
  setPositionOverrides,
  showOverrides,
  setShowOverrides,
  onCancelKp,
  exchangeRates,
  markupConstants,
  pricing,
}: Props) {
  return (
    <div className="px-4 py-2.5 border-t border-gray-100 flex items-center gap-3">
      {isProcurement ? (
        <ProcurementActions
          requestId={request.id}
          requestNumber={request.requestNumber}
          clientCode={request.client?.code ?? null}
          visibleItems={visibleItems}
          uploadRef={uploadRef}
          isUploading={isUploading}
          uploadResult={uploadResult}
          onUpload={onUpload}
        />
      ) : isRos ? (
        <Link
          to={`/requests/${request.id}/quotes-overview`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"
            />
          </svg>
          Все квоты
        </Link>
      ) : !kpMode ? (
        <>
          {!canCreateKp ? null : pricing.ready ? (
            <button
              onClick={onEnterKp}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              Выставить КП
            </button>
          ) : (
            // Курсы/коэффициенты не загрузились: кнопки «Выставить КП» нет —
            // считать себестоимость нечем. Ссылка «Выбор квот» остаётся, она
            // ведёт на отдельную страницу и от курсов не зависит.
            <PricingNotReady isLoading={pricing.isLoading} onRetry={pricing.retry} />
          )}
          <Link
            to={`/requests/${request.id}/quotes-selection`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
              />
            </svg>
            Выбор квот
          </Link>
        </>
      ) : exchangeRates && markupConstants ? (
        <KpModePanel
          request={request}
          visibleItems={visibleItems}
          selectedPrices={selectedPrices}
          setSelectedPrices={setSelectedPrices}
          kpMarkups={kpMarkups}
          setKpMarkups={setKpMarkups}
          positionOverrides={positionOverrides}
          setPositionOverrides={setPositionOverrides}
          showOverrides={showOverrides}
          setShowOverrides={setShowOverrides}
          onCancel={onCancelKp}
          exchangeRates={exchangeRates}
          markupConstants={markupConstants}
        />
      ) : (
        // Недостижимо при нормальной работе: режим КП включается только через
        // гейт готовности. Ветка нужна как страховка на случай, если кеш курсов
        // сбросился уже после входа в режим.
        <PricingNotReady isLoading={pricing.isLoading} onRetry={pricing.retry} />
      )}
    </div>
  )
}
